/**
 * Landing route for unauthenticated visitors.
 *
 * Authenticated users are redirected to their appropriate zone
 * (`/admin` or `/learn`) based on resolved scopes. This page is only
 * rendered for guests.
 *
 * @module routes/landing
 */

import { defaultRedirectForScopes } from '@learnai/api/auth';
import { useAuth } from '@learnai/api/auth-provider';
import { BrandLogo } from '@learnai/ui/components/blocks/brand-logo';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent } from '@learnai/ui/components/ui/card';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { BookOpenIcon, LineChartIcon, SparklesIcon } from 'lucide-react';
import { useEffect } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Feature {
	title: string;
	description: string;
	icon: React.ReactNode;
	accentBg: string;
	accentText: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FEATURES: Feature[] = [
	{
		title: 'AI-Powered Education',
		description: 'Every student gets Kyros — a patient, adaptive educator that meets them where they are.',
		icon: <SparklesIcon className="size-5" />,
		accentBg: 'bg-chart-1/15',
		accentText: 'text-chart-1',
	},
	{
		title: 'Real-Time Progress',
		description: 'Instructors monitor mastery scores and engagement as students learn.',
		icon: <LineChartIcon className="size-5" />,
		accentBg: 'bg-chart-2/15',
		accentText: 'text-chart-2',
	},
	{
		title: 'Flexible Curriculum',
		description: 'Create lessons with any source material — PDFs, links, or inline text.',
		icon: <BookOpenIcon className="size-5" />,
		accentBg: 'bg-chart-3/15',
		accentText: 'text-chart-3',
	},
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Landing() {
	const { isAuthenticated, isLoading, scopes } = useAuth();

	// -----------------------------------------------------------------------
	// Redirect authenticated users to their zone
	// -----------------------------------------------------------------------

	useEffect(() => {
		if (!isLoading && isAuthenticated) {
			const destination = defaultRedirectForScopes(scopes);
			window.location.href = destination;
		}
	}, [isLoading, isAuthenticated, scopes]);

	if (isLoading || isAuthenticated) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		);
	}

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="flex min-h-screen flex-col">
			{/* ----------------------------------------------------------------- */}
			{/* Top-left brand */}
			{/* ----------------------------------------------------------------- */}
			<nav className="px-6 pt-6">
				<a href="/">
					<BrandLogo size="sm" />
				</a>
			</nav>

			{/* ----------------------------------------------------------------- */}
			{/* Hero */}
			{/* ----------------------------------------------------------------- */}
			<header className="flex flex-col items-center gap-6 px-6 pt-16 pb-16 text-center">
				<h1 className="max-w-2xl text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
					Learn smarter with Kyros — an educator that&nbsp;
					<span className="text-primary">adapts&nbsp;to&nbsp;you</span>
				</h1>

				<p className="max-w-lg text-lg text-muted-foreground">
					LearnAI pairs every student with Kyros, a personal educator, giving instructors superpowers to monitor
					progress and tailor instruction at scale.
				</p>

				<div className="mt-4 flex items-center gap-4">
					<a href="/login">
						<Button size="lg">Get started</Button>
					</a>
					<a href="/login">
						<Button variant="outline" size="lg">
							Sign in
						</Button>
					</a>
				</div>
			</header>

			{/* ----------------------------------------------------------------- */}
			{/* Features */}
			{/* ----------------------------------------------------------------- */}
			<section className="mx-auto grid w-full max-w-5xl gap-8 px-6 py-16 sm:grid-cols-3">
				{FEATURES.map((feature) => (
					<Card key={feature.title}>
						<CardContent className="flex flex-col items-start gap-3">
							<div
								className={`flex size-10 items-center justify-center rounded-lg ${feature.accentBg} ${feature.accentText}`}
								aria-hidden="true"
							>
								{feature.icon}
							</div>
							<h3 className="text-lg font-semibold text-foreground">{feature.title}</h3>
							<p className="text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
						</CardContent>
					</Card>
				))}
			</section>

			{/* ----------------------------------------------------------------- */}
			{/* Footer */}
			{/* ----------------------------------------------------------------- */}
			<footer className="mt-auto border-t px-6 py-8 text-center text-sm text-muted-foreground">
				<p>&copy; {Temporal.Now.plainDateISO().year} LearnAI. All rights reserved.</p>
			</footer>
		</div>
	);
}
